import type {
  AutomationRule,
  AutomationRun,
  CreateAutomationRuleInput,
  DryRunRequest,
  DryRunResult,
  Page,
  TicketSlaListResponse,
  UpdateAutomationRule,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

/*
 * Automation rules (`/admin/v1/automations`, S-07-01 … S-07-04, S-07-12) and the ticket SLA
 * panel (`GET /v1/tickets/{id}/sla`, S-07-08 — grouped here since both belong to Unit 07).
 */

const base = adminPath('/automations');

export interface AutomationRuleFilters {
  /** Omit to see every rule the caller may see (tenant admin); a project scopes to it. */
  projectId?: string;
  enabled?: boolean;
}

/** `GET /admin/v1/automations?projectId=&enabled=` */
export function useAutomationRules(
  filters: AutomationRuleFilters = {},
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const { request, keys } = useApiContext();
  return usePagedList<AutomationRule>({
    queryKey: keys.automations.list({ ...filters, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<AutomationRule>>('GET', base, {
        query: { ...filters, ...pageQuery(cursor, pageSize) },
        signal,
      }),
  });
}

/** `GET /admin/v1/automations/{id}` */
export function useAutomationRule(ruleId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.automations.detail(ruleId ?? ''),
    queryFn: ({ signal }) => request<AutomationRule>('GET', `${base}/${ruleId}`, { signal }),
    enabled: ruleId !== undefined,
  });
}

export function useCreateAutomationRule() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateAutomationRuleInput) =>
      request<AutomationRule>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.automations.all),
  });
}

export function useUpdateAutomationRule() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateAutomationRule }) =>
      request<AutomationRule>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: (_rule, { id }) => invalidate(keys.automations.all, keys.automations.detail(id)),
  });
}

export function useDeleteAutomationRule() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${base}/${id}`),
    onSuccess: (_void, id) => {
      remove(keys.automations.detail(id));
      return invalidate(keys.automations.all);
    },
  });
}

export interface AutomationRunFilters {
  status?: AutomationRun['status'];
  ticketId?: string;
}

/** `GET /admin/v1/automations/{id}/runs?status=&ticketId=`, newest first. */
export function useAutomationRuns(
  ruleId: string | undefined,
  filters: AutomationRunFilters = {},
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const { request, keys } = useApiContext();
  return usePagedList<AutomationRun>({
    queryKey: keys.automations.runs(ruleId ?? '', { ...filters, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<AutomationRun>>('GET', `${base}/${ruleId}/runs`, {
        query: { ...filters, ...pageQuery(cursor, pageSize) },
        signal,
      }),
    enabled: ruleId !== undefined,
  });
}

/** `POST /admin/v1/automations/{id}/test` (S-07-12): dry-run against one ticket. */
export function useTestAutomationRule(ruleId: string | undefined) {
  const { request } = useApiContext();
  return useMutation({
    mutationFn: (input: DryRunRequest) =>
      request<DryRunResult>('POST', `${base}/${ruleId}/test`, { body: input }),
  });
}

/** `GET /v1/tickets/{id}/sla` (S-07-08): the ticket's SLA timers with `remainingMs` computed live. */
export function useTicketSla(ticketId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.ticket(ticketId ?? '').sla,
    queryFn: ({ signal }) =>
      request<TicketSlaListResponse>('GET', portalPath(`/tickets/${ticketId}/sla`), { signal }),
    enabled: ticketId !== undefined,
  });
}
